// @ts-check
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
} from "react"
import { useSession } from "../../../../../hooks/auth/useSession.hook"
import { Table } from "../../../../../components/table/Table.component"
import {
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table"
import useisHoldingModifierDown from "../../../../../hooks/useIsHoldingModifierDown"

import { EmptyTableMessage } from "../../../../../components/compounds/customMessages/EmptyTableMessage.compound"
import { useTaskReportsFilters } from "../../../../../hooks/reports/useTaskReportsFilters.hook"
import { buildClientTasksTableColumns } from "./ClientTasksTable.columns"
import { useClientTasksTableFilters } from "./ClientTasksTable.filters"
import { getPrimarySortValue } from "../../../../../utils/tables/sorting.utils"

/**
 * @type {React.ForwardRefExoticComponent<
 *   import("./ClientTasksTable.controller.types").ClientTasksTableProps &
 *   React.RefAttributes<import("./ClientTasksTable.controller.types").ClientTasksReportTableControllerRef>
 * >}
 */
const ClientTasksTableController = forwardRef((props, ref) => {
  const {
    value,
    contextMenuRef,
    setContextMenuTarget,
    updatingTask,
    categories,
    modules,
    projects,
    teamMembers,
    isLoading,
    onRowCountChange,
    refreshData,
    hasMultipleProjects,
  } = props

  const { user } = useSession()
  const userIsAdmin = user?.isAdmin()

  const { columnFilters, setColumnFilters, getFilterValues, setFilter } =
    useTaskReportsFilters({
      projectName: "projectCode",
      teamMemberNameTag: "teamMemberNameTag",
      moduleId: "module",
      taskName: "task",
    })

  const isHoldingModifierDown = useisHoldingModifierDown()

  const {
    teamMemberFilterTemplate,
    categoryFilterTemplate,
    moduleFilterTemplate,
    clientFilterTemplate,
    taskNameFilterTemplate,
  } = useClientTasksTableFilters({
    teamMembers,
    categories,
    modules,
    projects,
    loading: isLoading,
  })

  const columns = useMemo(
    () =>
      buildClientTasksTableColumns({
        isHoldingModifierDown,
        contextMenuRef,
        setContextMenuTarget,
        userIsAdmin,
        updatingTask,
        hasMultipleProjects,
        teamMemberFilterTemplate,
        categoryFilterTemplate,
        moduleFilterTemplate,
        clientFilterTemplate,
        taskNameFilterTemplate,
      }),
    [
      isHoldingModifierDown,
      contextMenuRef,
      setContextMenuTarget,
      userIsAdmin,
      updatingTask,
      hasMultipleProjects,
      teamMemberFilterTemplate,
      categoryFilterTemplate,
      moduleFilterTemplate,
      clientFilterTemplate,
      taskNameFilterTemplate,
    ]
  )

  const table = useReactTable({
    data: value,
    columns,
    enableSortingRemoval: true,
    sortDescFirst: false,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    initialState: {
      columnPinning: {
        left: ["projectCode", "date"],
      },
    },
    state: {
      columnFilters,
      columnVisibility: {
        projectCode: hasMultipleProjects,
      },
    },
    onColumnFiltersChange: setColumnFilters,
  })

  useImperativeHandle(
    ref,
    () => ({
      getFilterValues: () => getFilterValues(table),
      getSortingValue: () => getPrimarySortValue(table.getState().sorting),
      setFilter: (/** @type {string} */ field, /** @type {unknown} */ value) =>
        setFilter(table, field, value),
      refreshData: () => refreshData(),
    }),
    [table, refreshData, getFilterValues, setFilter]
  )

  const rowCount = useMemo(
    () => table.getRowModel().rows.length,
    [table.getRowModel().rows]
  )

  useEffect(() => {
    if (!onRowCountChange) {
      return
    }

    onRowCountChange(rowCount)
  }, [columnFilters, onRowCountChange, rowCount])

  /**
   * @param {React.MouseEvent} e
   * @param {import("./ClientTasksTable.controller.types").ClientTasksTableRow} row
   */
  function handleContextMenu(e, row) {
    if (!userIsAdmin || !row.original || row.getIsGrouped()) {
      return
    }

    setContextMenuTarget(row.original)
    contextMenuRef?.current?.show(e)
  }

  return (
    <Table
      table={table}
      estimatedRowHeight={() => 37}
      isLoading={isLoading}
      emptyMessage={<EmptyTableMessage />}
      className="client-tasks__table"
      getIsRowSticky={(row) => row.getIsGrouped()}
      onContextMenu={handleContextMenu}
    />
  )
})

// Devtools display name
ClientTasksTableController.displayName = "ClientTasksTableController"
export default ClientTasksTableController
